import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { School, ShieldCheck, RefreshCw, Search, Loader2, Upload, Check, X } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, tone = "default", testid }) {
  const cls = tone === "danger" ? "border-rose-200 bg-rose-50/50" : "border-slate-200 bg-white";
  return (
    <div data-testid={testid} className={`rounded-lg border ${cls} p-4`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-2xl font-display font-semibold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

const YN = ({ ok }) => ok
  ? <Check className="w-3.5 h-3.5 text-emerald-600 inline" />
  : <X className="w-3.5 h-3.5 text-rose-600 inline" />;

const SOURCE_LABEL = { licence: "Licence", certification: "Certification", induction: "Induction", fm_list: "FM list (NTBEL+Police+WWCC)" };

function ClientRulesSection() {
  const [rules, setRules] = useState([]);
  const [catalog, setCatalog] = useState({ induction: [], licence: [], certification: [] });
  const [sel, setSel] = useState(null);        // {client_name, requirements}
  const [matrix, setMatrix] = useState(null);
  const [clientQ, setClientQ] = useState("");
  const [clientOpts, setClientOpts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newReq, setNewReq] = useState({ source: "licence", match: "" });

  const loadRules = () => api.get("/fm-guard/rules").then((r) => setRules(r.data.items || [])).catch(() => {});
  useEffect(() => {
    loadRules();
    api.get("/fm-guard/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!clientQ.trim()) { setClientOpts([]); return; }
    const t = setTimeout(() =>
      api.get(`/fm-guard/clients?q=${encodeURIComponent(clientQ)}`).then((r) => setClientOpts(r.data.items || [])).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [clientQ]);

  const openClient = (name) => {
    const existing = rules.find((r) => r.client_name === name);
    setSel(existing ? { ...existing } : { client_name: name, requirements: [] });
    setMatrix(null);
    setClientQ(""); setClientOpts([]);
    if (existing) loadMatrix(name);
  };

  const loadMatrix = (name) =>
    api.get(`/fm-guard/matrix?client=${encodeURIComponent(name)}`).then((r) => setMatrix(r.data)).catch(() => {});

  const addReq = () => {
    if (newReq.source !== "fm_list" && !newReq.match) { toast.error("Pick the licence / cert / induction name"); return; }
    const label = newReq.source === "fm_list" ? "NSW FM list" : newReq.match;
    setSel({ ...sel, requirements: [...sel.requirements, { label, source: newReq.source, match: newReq.match }] });
    setNewReq({ source: newReq.source, match: "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/fm-guard/rules", sel);
      toast.success(`${sel.client_name}: ${sel.requirements.length} requirement(s) saved`);
      loadRules();
      if (sel.requirements.length) loadMatrix(sel.client_name); else { setSel(null); setMatrix(null); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="fm-rules-section">
      <div>
        <div className="text-xs font-medium text-slate-600">Key client requirements</div>
        <div className="text-[11px] text-slate-400 mt-0.5">
          Employees missing any required licence, certification or induction go <span className="text-rose-600 font-medium">red</span> for
          that client and are flagged the moment they're assigned one of its AroFlo tasks.
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {rules.map((r) => (
          <button key={r.client_name} onClick={() => openClient(r.client_name)}
                  data-testid={`fm-rule-chip-${r.client_name.slice(0, 20)}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${sel?.client_name === r.client_name ? "bg-gbe text-white border-gbe" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
            {r.client_name} · {r.requirements.length}
          </button>
        ))}
      </div>
      <div className="relative max-w-md">
        <div className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input data-testid="fm-rules-client-search" value={clientQ} onChange={(e) => setClientQ(e.target.value)}
                 placeholder="Search a client to configure requirements…"
                 className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" />
        </div>
        {clientOpts.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-auto">
            {clientOpts.map((c) => (
              <button key={c.name} onClick={() => openClient(c.name)}
                      className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50">{c.name}</button>
            ))}
          </div>
        )}
      </div>

      {sel && (
        <div className="border border-slate-200 rounded-lg p-3 space-y-3">
          <div className="text-sm font-medium text-slate-800">{sel.client_name}</div>
          <div className="space-y-1.5">
            {sel.requirements.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-slate-50">
                <span className="text-slate-700">
                  <Badge variant="outline" className="text-[10px] mr-2">{SOURCE_LABEL[r.source] || r.source}</Badge>
                  {r.label}
                </span>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400 hover:text-rose-600"
                        data-testid={`fm-req-remove-${i}`}
                        onClick={() => setSel({ ...sel, requirements: sel.requirements.filter((_, j) => j !== i) })}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            {sel.requirements.length === 0 && <div className="text-xs text-slate-400">No requirements yet.</div>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="fm-req-source" value={newReq.source}
                    onChange={(e) => setNewReq({ source: e.target.value, match: "" })}
                    className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white">
              <option value="licence">Licence</option>
              <option value="certification">Certification</option>
              <option value="induction">Induction</option>
              <option value="fm_list">FM list (all 3 checks)</option>
            </select>
            {newReq.source !== "fm_list" && (
              <select data-testid="fm-req-match" value={newReq.match}
                      onChange={(e) => setNewReq({ ...newReq, match: e.target.value })}
                      className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white min-w-56">
                <option value="">— pick {newReq.source} —</option>
                {(catalog[newReq.source] || []).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            )}
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={addReq} data-testid="fm-req-add-btn">
              Add requirement
            </Button>
            <Button size="sm" className="h-8 text-xs" onClick={save} disabled={saving} data-testid="fm-rules-save-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save rules"}
            </Button>
          </div>

          {matrix && matrix.requirements.length > 0 && (
            <div className="overflow-x-auto max-h-80 overflow-y-auto border border-slate-100 rounded-md"
                 data-testid="fm-matrix">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left font-medium px-3 py-2">
                      Employee <span className="normal-case text-rose-600">({matrix.red_count} red — cannot be assigned)</span>
                    </th>
                    {matrix.requirements.map((r) => <th key={r} className="text-center font-medium px-2 py-2">{r}</th>)}
                    <th className="text-center font-medium px-2 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.rows.map((row) => (
                    <tr key={row.employee} className={`border-b border-slate-50 ${row.red ? "bg-rose-50/40" : ""}`}>
                      <td className="px-3 py-1 font-medium text-slate-800">{row.employee}</td>
                      {row.checks.map((c) => (
                        <td key={c.label} className="px-2 py-1 text-center" title={c.fail || "OK"}>
                          {c.fail ? <X className="w-3.5 h-3.5 text-rose-600 inline" /> : <Check className="w-3.5 h-3.5 text-emerald-600 inline" />}
                        </td>
                      ))}
                      <td className="px-2 py-1 text-center">
                        {row.red
                          ? <Badge className="bg-rose-600 text-white text-[10px]">RED</Badge>
                          : <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">OK</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function FmGuardPanel() {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [running, setRunning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const fileRef = useRef(null);

  const load = (all = showAll) =>
    api.get(`/fm-guard/overview?status=${all ? "all" : "open"}`).then((r) => setData(r.data)).catch(() => {});
  const loadClients = (query = "") =>
    api.get(`/fm-guard/clients?q=${encodeURIComponent(query)}`).then((r) => setClients(r.data.items || [])).catch(() => {});

  useEffect(() => { load(); loadClients(); }, []);
  useEffect(() => {
    const t = setTimeout(() => loadClients(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const uploadRoster = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: r } = await api.post("/fm-guard/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Roster loaded — ${r.total} people, ${r.compliant} compliant, ${r.non_compliant} non-compliant`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const runCheck = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post("/fm-guard/run-check");
      toast.success(`Checked ${r.jobs_checked} open tasks — ${r.new_flags} new flag(s), ${r.flags_open} open`);
      load();
    } catch { toast.error("Check failed"); }
    setRunning(false);
  };

  const toggleClient = async (name, required) => {
    try {
      await api.put("/fm-guard/clients", { client_name: name, required });
      toast.success(`${name}: school-FM ${required ? "required" : "not required"}`);
      loadClients(q); load();
    } catch { toast.error("Update failed"); }
  };

  const resolve = async (id) => {
    try {
      await api.put(`/fm-guard/flags/${id}/resolve`, { note: "Resolved manually" });
      toast.success("Flag resolved");
      load();
    } catch { toast.error("Resolve failed"); }
  };

  const flags = data?.flags || [];
  const roster = data?.roster || [];
  return (
    <div className="space-y-4" data-testid="fm-guard-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <School className="w-4 h-4 text-sky-700" /> Key Client Compliance Guard
            <span className="text-[10px] font-normal text-slate-400">FM list · licences · certs · inductions</span>
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
            Configure required licences, certifications, inductions and the NSW FM list per key client.
            Any open AroFlo task at those clients assigned to a red (non-compliant) worker is flagged and the
            worker + BU manager are emailed. Runs after every AroFlo sync + nightly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden"
                 onChange={uploadRoster} data-testid="fm-roster-file-input" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid="fm-upload-roster-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
            Upload FM list (CSV)
          </Button>
          <Button size="sm" data-testid="fm-run-check-btn" onClick={runCheck} disabled={running}>
            {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Run check now
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Open flags" value={data?.stats?.open_flags ?? "—"}
              tone={(data?.stats?.open_flags ?? 0) > 0 ? "danger" : "default"} testid="fm-stat-open" />
        <Stat label="On FM list" value={data?.stats?.roster_total ?? "—"} testid="fm-stat-roster" />
        <Stat label="Fully compliant" value={data?.stats?.roster_compliant ?? "—"} testid="fm-stat-compliant" />
        <Stat label="School-FM clients" value={data?.stats?.required_clients ?? "—"} testid="fm-stat-clients" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50/60">
          <span className="text-xs font-medium text-slate-600">Flagged assignments</span>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
            <input type="checkbox" data-testid="fm-show-all" checked={showAll}
                   onChange={(e) => { setShowAll(e.target.checked); load(e.target.checked); }} />
            Include resolved
          </label>
        </div>
        {flags.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" /> No non-compliant assignments detected.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-medium px-3 py-2">Worker</th>
                  <th className="text-left font-medium px-3 py-2">Task</th>
                  <th className="text-left font-medium px-3 py-2">Client</th>
                  <th className="text-left font-medium px-3 py-2">Due</th>
                  <th className="text-left font-medium px-3 py-2">Why non-compliant</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {flags.map((f) => (
                  <tr key={f.id} data-testid={`fm-flag-${f.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-medium text-slate-800">{f.technician}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{f.job_number || f.job_ref}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{f.client_name}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{(f.due_date || "—").slice(0, 10)}</td>
                    <td className="px-3 py-2 text-xs text-rose-700">{(f.reasons || [f.reason]).join("; ")}</td>
                    <td className="px-3 py-2 text-xs">
                      {f.status === "open"
                        ? <span className="text-rose-600 font-medium">open</span>
                        : <span className="text-slate-400">{String(f.status).replace("_", " ")}</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {f.status === "open" && (
                        <Button size="sm" variant="outline" className="h-6 text-[11px]"
                                data-testid={`fm-resolve-${f.id}`} onClick={() => resolve(f.id)}>
                          Resolve
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50/60">
          <span className="text-xs font-medium text-slate-600">
            Approved tradesperson list
            {data?.roster_uploaded_at && (
              <span className="text-slate-400 font-normal ml-2">
                uploaded {String(data.roster_uploaded_at).slice(0, 10)}
              </span>
            )}
          </span>
        </div>
        {roster.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            No FM list loaded yet — upload the TradesPersonList CSV from NSW Infrastructure FM Web.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-medium px-3 py-2">Name</th>
                  <th className="text-left font-medium px-3 py-2">Trade</th>
                  <th className="text-center font-medium px-3 py-2">NTBEL</th>
                  <th className="text-center font-medium px-3 py-2">Police</th>
                  <th className="text-center font-medium px-3 py-2">WWCC</th>
                  <th className="text-left font-medium px-3 py-2">Licence expiry</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {roster.map((p) => (
                  <tr key={p.id} data-testid={`fm-roster-${p.id}`} className="border-b border-slate-100">
                    <td className="px-3 py-1.5 font-medium text-slate-800">{p.display_name}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-600">{p.service_types}</td>
                    <td className="px-3 py-1.5 text-center"><YN ok={p.ntbel} /></td>
                    <td className="px-3 py-1.5 text-center"><YN ok={p.police} /></td>
                    <td className="px-3 py-1.5 text-center"><YN ok={p.wwcc} /></td>
                    <td className="px-3 py-1.5 text-xs text-slate-600">{p.license_expiry || "—"}</td>
                    <td className="px-3 py-1.5">
                      {p.compliant
                        ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">compliant</Badge>
                        : <Badge className="bg-rose-100 text-rose-700 text-[10px]"
                                 title={(p.non_compliant_reasons || []).join("; ")}>non-compliant</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ClientRulesSection />

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-xs font-medium text-slate-600 mb-2">School-FM clients (sites where the FM list applies)</div>
        <div className="flex items-center gap-2 mb-3 border border-slate-200 rounded-md px-2.5 py-1.5 max-w-md">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input data-testid="fm-client-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search clients to add or remove…"
                 className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" />
        </div>
        <div className="space-y-1.5">
          {clients.map((c) => (
            <div key={c.name} className="flex items-center justify-between gap-3 text-sm py-1 border-b border-slate-50">
              <span className="text-slate-700">
                {c.name}
                {c.suggested && !c.required && (
                  <span className="ml-2 text-[10px] text-amber-600">looks school/FM-related — review</span>
                )}
              </span>
              <Switch checked={c.required} data-testid={`fm-client-toggle-${c.name.slice(0, 20)}`}
                      onCheckedChange={(v) => toggleClient(c.name, v)} />
            </div>
          ))}
          {clients.length === 0 && <div className="text-xs text-slate-400">No matching clients.</div>}
        </div>
      </div>
    </div>
  );
}

export default FmGuardPanel;
